// /industrial-estates/$slug — TH per-estate detail page (Phase P-INDUSTRIAL-ESTATES)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { INDUSTRIAL_ESTATES, INDUSTRIAL_ESTATE_SLUGS, getIndustrialEstate } from "@/data/industrial-estates";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/industrial-estates/$slug")({
  beforeLoad: ({ params }) => {
    if (!INDUSTRIAL_ESTATE_SLUGS.includes(params.slug)) throw notFound();
  },
  head: ({ params }) => {
    const e = getIndustrialEstate(params.slug)!;
    const thPath = `/industrial-estates/${e.slug}`;
    const enPath = `/en/industrial-estates/${e.slug}`;
    const title = `${e.nameTh} — Notary · แปล · Work Permit · MFA ครบในที่เดียว | NYC`;
    const desc = `บริการครบวงจรสำหรับโรงงานใน${e.nameTh} (${e.province}) — รับเอกสารถึงโรงงาน, แปล EN/JP/CN/KR, ยื่น MFA, work permit, BOI report. ${e.highlights} — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    const steps = [
      { name: `Site visit ใน${e.nameTh}`, text: `ทีม NYC เข้ารับเอกสารถึงโรงงานใน${e.nameTh} หรือนัดที่ ${e.nearbyAirport} (${e.distanceToBkkKm} กม. จาก กทม.)` },
      { name: "ตรวจประเภทเอกสาร + เสนอราคา", text: `แยก notary, แปล, รับรอง MFA, work permit — เสนอราคาภายใน 4 ชั่วโมง ครอบคลุมภาษา ${e.topNationalities.join(" / ")}` },
      { name: "ดำเนินการ + รายงานสถานะ", text: `Notary same-day, แปล 1-2 วัน, MFA 2-3 วัน, work permit 7-10 วัน — รายงานทุก milestone ผ่าน LINE / Email` },
      { name: "ส่งคืนถึงโรงงาน", text: `Courier ตรงถึงโรงงานใน${e.nameTh} หรือ EMS ทั่วประเทศ ฟรีเมื่อยอดเกิน 5,000 บาท` },
    ];
    const faqs = [
      { q: `NYC เคยให้บริการในโรงงาน${e.nameTh}ไหม?`,
        a: `เคย — ${e.highlights}` },
      { q: `รับเอกสารใน${e.nameTh}ใช้เวลาเท่าไหร่?`,
        a: `Same-day pickup ภายใน 4 ชั่วโมงเมื่อยืนยัน — จาก กทม.ใช้เวลาขับ ~${Math.ceil(e.distanceToBkkKm / 60)} ชั่วโมง หรือใช้ทีมสาขาใกล้สุด ขึ้นกับนิคม` },
      { q: `รองรับภาษาอะไรบ้างสำหรับ${e.nameTh}?`,
        a: `รองรับ ${e.topNationalities.join(" · ")} — มีนักแปล ISO 17100 และล่ามได้ในไซต์` },
      { q: `ค่าบริการมีเรียกเก็บค่าเดินทางเพิ่มไหม?`,
        a: `ไม่มี — ค่าบริการเหมาเดียวกับลูกค้ากรุงเทพฯ Courier รวมในยอดงาน เริ่มต้น notary 1,500 บาท แปล 800/หน้า MFA 3,000 บาท/ฉบับ` },
      { q: `Work Permit ใหม่สำหรับวิศวกรใน${e.nameTh}กี่วัน?`,
        a: `7-10 วันราชการ — ยื่นสำนักงานจัดหางาน${e.province} หรือ One Stop Service Center (กรณี BOI) NYC ดูแล Power of Attorney + เอกสารบริษัทครบ` },
      { q: `${e.nameTh}อยู่ใกล้ท่าเรือ/สนามบินไหน?`,
        a: `ท่าเรือใกล้สุด: ${e.nearbyPort} · สนามบินใกล้สุด: ${e.nearbyAirport} — ${e.distanceToBkkKm} กม. จากกรุงเทพฯ` },
    ];
    const breadcrumbs = [
      { name: "หน้าแรก", path: "/" },
      { name: "นิคมอุตสาหกรรม", path: "/industrial-estates" },
      { name: e.nameTh, path: thPath },
    ];
    const localBusiness = {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org", "@type": "LegalService",
        "@id": `${SITE}${thPath}#local`,
        name: `NYC Legal & Notary — บริการใน${e.nameTh}`,
        url: `${SITE}${thPath}`, telephone: "+66922429293", priceRange: "฿฿",
        areaServed: [
          { "@type": "AdministrativeArea", name: e.province },
          { "@type": "Place", name: e.nameTh },
        ],
        address: { "@type": "PostalAddress", addressCountry: "TH", addressRegion: e.region, addressLocality: e.province },
        geo: { "@type": "GeoCoordinates", latitude: e.lat, longitude: e.lng },
        geoRadius: "50000",
        knowsLanguage: ["th", "en", "ja", "zh", "ko"],
      }),
    };
    return {
      meta: [
        { title: ctaTitle(title, "/industrial-estates/$slug", "th") }, { name: "description", content: ctaDesc(desc, "/industrial-estates/$slug", "th")},
        { name: "keywords", content: `${e.nameTh}, ${e.nameEn}, notary ${e.province}, work permit ${e.province}, แปลเอกสารโรงงาน ${e.province}, MFA ${e.nameTh}, BOI ${e.nameTh}` },
        { property: "og:title", content: ctaTitle(title, "/industrial-estates/$slug", "th")}, { property: "og:description", content: ctaDesc(desc, "/industrial-estates/$slug", "th")},
        { property: "og:url", content: `${SITE}${thPath}` }, { property: "og:locale", content: "th_TH" },
      ],
      links: canonicalAndHreflang(thPath, enPath),
      scripts: [
        ...buildLandingSchemas({
          path: thPath, title, description: desc,
          serviceType: "Industrial Estate Legal & Translation Service",
          priceFromThb: 1500, steps, faqs, breadcrumbs,
        }),
        localBusiness,
      ],
    };
  },
  component: IndustrialEstateDetailTh,
});

function IndustrialEstateDetailTh() {
  const { slug } = Route.useParams();
  const e = getIndustrialEstate(slug)!;
  const nearby = INDUSTRIAL_ESTATES.filter((x) => x.region === e.region && x.slug !== e.slug).slice(0, 6);
  return (
    <>
      <ServiceLanding
        locale="th"
        badge={`นิคมอุตสาหกรรม · ${e.province}`}
        title={`บริการครบวงจรสำหรับโรงงานใน${e.nameTh}`}
        subtitle={`${e.highlights} ผู้ดำเนินการ: ${e.operator} · ${e.zoneType} · ท่าเรือ ${e.nearbyPort} · สนามบิน ${e.nearbyAirport}`}
        quickAnswer={{
          q: `โรงงานใน${e.nameTh}จะใช้บริการ NYC อย่างไร?`,
          a: `LINE @nyc-notary หรือโทร 092-242-9293 — ทีมเข้ารับเอกสารถึงโรงงานใน${e.nameTh} วันถัดไปได้งานแปลพร้อม Notary, ส่ง MFA ภายใน 3 วัน, รวม work permit + BOI report. รับประกัน 99.6% ผ่านการตรวจครั้งแรก`,
        }}
        keyFacts={[
          { k: "ผู้ดำเนินการ", v: e.operator },
          { k: "ประเภทเขต", v: e.zoneType },
          { k: "อุตสาหกรรมหลัก", v: e.industries.slice(0, 3).join(" · ") },
          { k: "สัญชาติแรงงานต่างชาติ", v: e.topNationalities.slice(0, 4).join(" · ") },
          { k: "ท่าเรือใกล้สุด", v: e.nearbyPort },
          { k: "สนามบินใกล้สุด", v: e.nearbyAirport },
          { k: "ระยะจาก กทม.", v: `${e.distanceToBkkKm} กม.` },
          { k: "เริ่มต้น (Notary)", v: "1,500 บาท" },
        ]}
        heroBullets={[
          `รับเอกสารถึงโรงงานใน${e.nameTh} ทุกวันทำการ`,
          `แปล Technical EN/JP/CN/KR · ISO 17100 quality cycle`,
          `Work Permit + BOI Visa สำหรับ ${e.topNationalities.slice(0, 3).join(" / ")}`,
          `MFA Legalisation + Embassy ภายใน 5-7 วัน`,
          `ครอบคลุม ${e.industries.length} กลุ่มอุตสาหกรรมในไซต์`,
        ]}
        whoFor={{
          th: [
            `HR Director ของโรงงานใน${e.nameTh} ที่ต้องต่อ work permit วิศวกรต่างชาติ`,
            `ฝ่ายส่งออกที่ต้องการ COO + MFA legalisation รายเดือน`,
            `ทีม BOI ที่ต้องรายงานประจำปีและขอแก้ไขประเภทกิจการ`,
          ],
          foreign: [
            `Japanese/Chinese/Korean engineers needing renewed work permits at ${e.nameEn}`,
            `Plant managers requiring certified translation of HR contracts and ISO manuals`,
            `Expat directors handling Board Resolutions and POAs for ${e.nameEn} operations`,
          ],
        }}
        steps={[
          { name: `Site visit ใน${e.nameTh}`, text: `ทีมรับเอกสารถึงโรงงาน หรือ EMS จาก ${e.province}` },
          { name: "เสนอราคา 4 ชั่วโมง", text: `แยก notary / แปล / MFA / work permit ราคาเหมาเดียวกับ กทม.` },
          { name: "ดำเนินการ + รายงานสถานะ", text: `Notary same-day · แปล 1-2 วัน · MFA 2-3 วัน · Work Permit 7-10 วัน` },
          { name: "ส่งคืนถึงโรงงาน", text: `Courier ตรงหรือ EMS ฟรีเมื่อยอด > 5,000 บาท` },
        ]}
        pricing={[
          { label: "Notary (POA / Board Resolution)", price: "1,500 - 8,000", note: "Notarial Services Attorney" },
          { label: "Certified Translation EN/JP/CN/KR", price: "800/หน้า", note: "ISO 17100" },
          { label: "MFA Legalisation", price: "3,000/ฉบับ", note: "รวมค่าธรรมเนียม" },
          { label: "Work Permit ใหม่ (BOI/IEAT)", price: "12,000", note: "7-10 วันราชการ" },
          { label: "Apostille (Hague countries)", price: "5,000/ฉบับ", note: "ผ่าน MFA + Embassy" },
        ]}
        faqs={[
          { q: `NYC มีเส้นทาง courier ประจำใน${e.nameTh}ไหม?`,
            a: `มี — เส้นทาง ${e.province}↔กรุงเทพฯ วันละ 1 รอบ (เช้า-เย็น) ส่งเอกสารถึงโรงงานในวันเดียว` },
          { q: `ค่าบริการนอกกรุงเทพฯ แพงกว่าไหม?`,
            a: `ไม่ — เหมาเดียวกับ กทม. ค่า courier รวมในยอดงาน` },
          { q: `Work Permit เร่งด่วนใน${e.nameTh}ทำได้กี่วัน?`,
            a: `BOI/IEAT One Stop Service 3-5 วัน · ปกติ 7-10 วันราชการ` },
          { q: `แปล MSDS / HAZOP / ISO Manual รับไหม?`,
            a: `รับ — มีนักแปลที่มี background วิศวกรรมและ ISO 17100 quality cycle` },
        ]}
        relatedLinks={[
          { to: "/work-permit", label: "Work Permit + BOI Visa" },
          { to: "/translate", label: "Certified Translation EN/JP/CN/KR" },
          { to: "/translate/mfa", label: "MFA Legalisation" },
          { to: "/notary", label: "Notarial Services Attorney" },
          { to: "/apostille", label: "Apostille (Hague)" },
          { to: "/apec", label: "APEC Card สำหรับผู้บริหารโรงงาน" },
        ]}
      />

      {nearby.length > 0 && (
        <section className="px-4 py-12">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-2xl font-extrabold sm:text-3xl">นิคมใกล้เคียงใน{e.region}</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {nearby.map((n) => (
                <Link key={n.slug} to="/industrial-estates/$slug" params={{ slug: n.slug }}
                  className="rounded-xl border border-border bg-card p-4 hover:border-primary"
                  data-track-zone="industrial-detail-nearby-th">
                  <div className="font-bold">{n.nameTh}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{n.province} · {n.zoneType}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
